import { showOperationError } from "../../lib/notifications";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { AdminAccount } from "../../lib/api";
import { ApiError, adminApi } from "../../lib/api";
import { useAdminAuth } from "../../lib/auth";

const conflictMessage =
  "Данные изменены другим администратором. Перезагрузите актуальную версию.";

function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) return conflictMessage;
  return error instanceof Error
    ? error.message
    : "Не удалось выполнить действие";
}

export function AccessTab({ account }: { account: AdminAccount }) {
  const { token } = useAdminAuth();
  const queryClient = useQueryClient();
  const user = account.user;
  const [email, setEmail] = useState(user?.email ?? "");
  const [emailPending, setEmailPending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordPending, setPasswordPending] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    setEmail(user?.email ?? "");
  }, [account.id, user?.email, user?.updatedAt]);

  useEffect(() => {
    setTemporaryPassword("");
    setPasswordConfirmation("");
    setEmailError(null);
    setPasswordError(null);
  }, [account.id]);

  if (!user) {
    return (
      <div className="panel p-4 text-sm text-muted-foreground">
        Пользователь не привязан к аккаунту.
      </div>
    );
  }
  const expectedUserUpdatedAt = user.updatedAt;

  async function invalidateAccount() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", account.id],
      }),
      queryClient.invalidateQueries({ queryKey: ["admin", "accounts"] }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", account.id, "audit"],
      }),
    ]);
  }

  async function handleEmailSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (emailPending) return;
    setEmailError(null);
    setEmailPending(true);
    try {
      await adminApi.updateProfile(token!, account.id, {
        expectedUserUpdatedAt,
        email: email.trim(),
      });
      toast.success("Email пользователя сохранён");
      await invalidateAccount();
    } catch (error) {
      showOperationError(error, errorMessage(error), setEmailError);
    } finally {
      setEmailPending(false);
    }
  }

  async function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (passwordPending) return;
    if (temporaryPassword.length < 12 || temporaryPassword.length > 128) {
      setPasswordError(
        "Временный пароль должен содержать от 12 до 128 символов",
      );
      return;
    }
    if (temporaryPassword !== passwordConfirmation) {
      setPasswordError("Пароли не совпадают");
      return;
    }

    setPasswordError(null);
    setPasswordPending(true);
    try {
      await adminApi.setTemporaryPassword(token!, account.id, {
        expectedUserUpdatedAt,
        temporaryPassword,
      });
      setTemporaryPassword("");
      setPasswordConfirmation("");
      toast.success("Временный пароль установлен");
      await invalidateAccount();
    } catch (error) {
      showOperationError(error, errorMessage(error), setPasswordError);
    } finally {
      setPasswordPending(false);
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">Email пользователя</h2>
        <form className="mt-4" onSubmit={handleEmailSubmit}>
          <label className="block text-sm font-medium">
            <span className="mb-1.5 block">Email пользователя</span>
            <input
              className="control"
              type="email"
              required
              value={email}
              disabled={emailPending}
              autoComplete="off"
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <button
            className="mt-3 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
            type="submit"
            disabled={emailPending}
          >
            {emailPending ? "Сохраняем..." : "Сохранить email"}
          </button>
          {emailError ? (
            <p className="mt-3 text-sm text-danger" role="alert">
              {emailError}
            </p>
          ) : null}
        </form>
      </section>

      <section className="panel p-4">
        <h2 className="text-lg font-semibold">Временный пароль</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          После входа клиент должен будет заменить временный пароль.
        </p>
        <form
          className="mt-4 space-y-3"
          noValidate
          onSubmit={handlePasswordSubmit}
        >
          <label className="block text-sm font-medium">
            <span className="mb-1.5 block">Временный пароль</span>
            <input
              className="control"
              type="password"
              minLength={12}
              maxLength={128}
              value={temporaryPassword}
              disabled={passwordPending}
              autoComplete="new-password"
              onChange={(event) => setTemporaryPassword(event.target.value)}
            />
          </label>
          <label className="block text-sm font-medium">
            <span className="mb-1.5 block">
              Подтверждение временного пароля
            </span>
            <input
              className="control"
              type="password"
              minLength={12}
              maxLength={128}
              value={passwordConfirmation}
              disabled={passwordPending}
              autoComplete="new-password"
              onChange={(event) => setPasswordConfirmation(event.target.value)}
            />
          </label>
          <button
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
            type="submit"
            disabled={passwordPending}
          >
            {passwordPending
              ? "Устанавливаем..."
              : "Установить временный пароль"}
          </button>
          {passwordError ? (
            <p className="text-sm text-danger" role="alert">
              {passwordError}
            </p>
          ) : null}
        </form>
      </section>
    </div>
  );
}
